import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Modal, Button, Badge, Row, Col } from 'react-bootstrap';

const PatientManagement = () => {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // History Modal State
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [historyAppointments, setHistoryAppointments] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        fetchPatients();
    }, [searchTerm]);

    const fetchPatients = async () => {
        setLoading(true);
        try {
            // Backend now strictly filters for role='Patient' and auto-discovers from appointments
            const response = await api.get(`/clinic/patients?search=${searchTerm}`);
            setPatients(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching patients:', error);
            setLoading(false);
        }
    };

    // HISTORY & DETAIL LOGIC
    const handleViewHistory = async (patient) => {
        setSelectedPatient(patient);
        setShowHistoryModal(true);
        setHistoryLoading(true);
        try {
            // Fetch appointments for this specific patient
            const response = await api.get('/appointments', {
                params: { patientId: patient._id }
            });
            setHistoryAppointments(response.data);
            setHistoryLoading(false);
        } catch (error) {
            console.error("Error fetching history", error);
            setHistoryLoading(false);
        }
    };

    const handleViewPrescription = async (apptId) => {
        try {
            const res = await api.get(`/prescriptions/${apptId}`);

            if (res.data) {
                const rx = res.data;
                // Prepare the full URL if it's a relative path - using the baseURL from api service logic (which defaults to /api, and the build target will handle proxy)
                // However, for images, we might still need a base URL if they are served from a different static path.
                // For now, removing the hardcoded production domain and making it relative.
                const fullImageUrl = rx.imageUrl && !rx.imageUrl.startsWith('http') ? rx.imageUrl : rx.imageUrl;

                const rxHtml = `
                    <div id="rx-print-content" class="rx-modal-container text-start" style="padding: 25px; font-family: 'Inter', 'Segoe UI', sans-serif; color: #334155; line-height: 1.5;">
                        <!-- HEADER: Heading Style, No Borders -->
                        <div class="text-center mb-4">
                            <h4 class="fw-bold text-primary mb-1" style="font-size: 1.6rem; letter-spacing: -0.5px;">${rx.clinicId?.name || 'Clinic'}</h4>
                            <p class="text-secondary mb-3" style="font-size: 0.85rem;"><i class="bi bi-geo-alt-fill me-1"></i> ${rx.clinicId?.address || ''}</p>
                            
                            <div class="mt-3 pt-2" style="border-top: 1.5px solid #e2e8f0; max-width: 150px; margin: 0 auto;"></div>
                            
                            <h6 class="fw-bold mt-3 mb-1 text-dark" style="font-size: 1rem;">Dr. ${rx.doctorId?.name.replace('Dr.', '')?.replace('dr.', '')?.replace('Mr.', '')?.replace('mr.', '')?.replace('Miss.', '')?.replace('miss.', '')?.replace('Mrs.', '')?.replace('mrs.', '')?.replace('Ms.', '')?.replace('ms.', '')}</h6>
                            <p class="text-primary fw-semibold" style="letter-spacing: 0.5px; font-size: 0.75rem;">${rx.doctorId?.specialization?.toUpperCase() || ''}</p>
                        </div>
                        
                        <div class="row mb-4 px-2">
                            <div class="col-6">
                                <label class="text-uppercase fw-bold text-secondary mb-1 d-block" style="font-size: 0.65rem;">Patient Name</label>
                                <p class="fw-bold mb-0" style="font-size: 0.95rem;">${selectedPatient?.name}</p>
                            </div>
                            <div class="col-6 text-end">
                                <label class="text-uppercase fw-bold text-secondary mb-1 d-block" style="font-size: 0.65rem;">Date</label>
                                <p class="fw-bold mb-0" style="font-size: 0.95rem;">${new Date(rx.createdAt).toLocaleDateString()}</p>
                            </div>
                        </div>

                        ${rx.imageUrl ? `
                            <div class="mb-4 text-center p-3 bg-white radius-10 shadow-sm border" style="background: #f8fafc !important;">
                                <label class="d-block text-uppercase fw-bold text-secondary mb-2" style="font-size: 0.65rem;">Handwritten Prescription</label>
                                <img src="${rx.imageUrl}" alt="Prescription" style="max-width: 100%; border-radius: 10px; box-shadow: 0 8px 20px rgba(0,0,0,0.08);" />
                            </div>
                        ` : ''}

                        ${rx.medicines && rx.medicines.length > 0 ? `
                            <div class="mb-4">
                                <h6 class="fw-bold text-uppercase text-primary mb-2 d-flex align-items-center" style="font-size: 0.8rem;">
                                    <span class="bg-primary text-white rounded-circle d-inline-flex justify-content-center align-items-center me-2" style="width: 22px; height: 22px; font-size: 11px;">Rx</span>
                                    Medications
                                </h6>
                                <div class="table-responsive radius-10 border overflow-hidden shadow-sm">
                                    <table class="table table-hover mb-0" style="font-size: 0.85rem;">
                                        <thead class="bg-light">
                                            <tr>
                                                <th class="ps-3 py-2" style="background: #f1f5f9; font-weight: 700;">Medicine</th>
                                                <th class="py-2" style="background: #f1f5f9; font-weight: 700;">Dosage</th>
                                                <th class="py-2" style="background: #f1f5f9; font-weight: 700;">Frequency</th>
                                                <th class="pe-3 py-2 text-end" style="background: #f1f5f9; font-weight: 700;">Duration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            ${rx.medicines.map(m => `
                                                <tr class="align-middle">
                                                    <td class="ps-3 fw-bold text-dark">${m.name}</td>
                                                    <td>${m.dosage}</td>
                                                    <td><span class="badge rounded-pill bg-primary-50 text-primary-700 px-2 py-1 border border-primary-100" style="font-size: 0.7rem;">${m.frequency}</span></td>
                                                    <td class="pe-3 text-end fw-medium" style="font-size: 0.8rem;">${m.duration || 15} days</td>
                                                </tr>
                                            `).join('')}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ` : ''}

                        ${rx.notes ? `
                            <div class="mt-3 p-3 radius-10 bg-warning-50 border-start border-warning border-4 shadow-sm">
                                <h6 class="fw-bold text-uppercase text-warning-700 mb-1 d-flex align-items-center" style="font-size: 0.75rem;">
                                    <i class="bi bi-info-circle-fill me-2"></i> Doctor's Advice
                                </h6>
                                <p class="mb-0 text-dark" style="font-size: 0.85rem; white-space: pre-wrap; font-style: italic; opacity: 0.9;">"${rx.notes}"</p>
                            </div>
                        ` : ''}
                        
                        <div class="mt-4 pt-3 border-top text-center opacity-50">
                            <p class="mb-0" style="font-size: 0.65rem;">Powered by Medoro Health System - Digital Record: ${rx._id.toUpperCase()}</p>
                        </div>
                    </div>
                `;

                Swal.fire({
                    title: 'Electronic Prescription',
                    width: 750,
                    html: rxHtml,
                    showCloseButton: true,
                    confirmButtonText: '<i class="bi bi-printer me-2"></i> Print Prescription',
                    customClass: {
                        confirmButton: 'btn btn-primary px-4 py-2 radius-10 shadow-sm fw-bold',
                        container: 'rx-detail-swal',
                        backdrop: 'swal2-backdrop-show'
                    },
                    buttonsStyling: false,
                    preConfirm: () => {
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(`
                            <html>
                                <head>
                                    <title>Prescription - ${rx._id}</title>
                                    <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
                                    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css">
                                    <style>
                                        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                                        body { font-family: 'Inter', sans-serif; background: white; }
                                        .rx-modal-container { width: 100%; max-width: 800px; margin: 0 auto; padding: 20px; }
                                        @media print {
                                            .btn, .swal2-close, .swal2-actions { display: none !important; }
                                            body { padding: 0; margin: 0; }
                                            .rx-modal-container { box-shadow: none !important; border: none !important; }
                                        }
                                    </style>
                                </head>
                                <body onload="window.print(); window.close();">
                                    ${rxHtml}
                                </body>
                            </html>
                        `);
                        printWindow.document.close();
                        return false; // Don't close Swami modal automatically
                    }
                });
            } else {
                Swal.fire('No Prescription', 'No prescription record found for this appointment.', 'warning');
            }
        } catch (err) {
            Swal.fire('Error', 'Could not open prescription. Please try again later.', 'error');
        }
    };

    return (
        <div className="card h-100 p-0 radius-12 fade-in-up">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    <div className="w-40-px h-40-px radius-8 bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-xl">
                        <Icon icon="solar:users-group-rounded-bold" />
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold text-dark">Patient Records</h5>
                        <p className="text-sm text-secondary mb-0">View patient history and interaction logs</p>
                    </div>
                </div>
                <div className="position-relative">
                    <input
                        type="text"
                        className="form-control ps-5 radius-8"
                        placeholder="Search patients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Icon icon="solar:magnifer-linear" className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary" />
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive">
                    <table className="table bordered-table mb-0">
                        <thead>
                            <tr>
                                <th>Patient Name</th>
                                <th>Contact Info</th>
                                <th>Account Created</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="4" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                            ) : patients.length === 0 ? (
                                <tr><td colSpan="4" className="text-center py-5 text-secondary">No patients found.</td></tr>
                            ) : (
                                patients.map(patient => (
                                    <tr key={patient._id} className="hover-bg-light transition-all">
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden bg-info-50 d-flex justify-content-center align-items-center text-info-600 fw-bold">
                                                    {patient.profileImage ? <img src={patient.profileImage} alt="" className="w-100 h-100 object-fit-cover" /> : patient.name.charAt(0)}
                                                </div>
                                                <span className="fw-medium text-dark">{patient.name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="text-sm text-dark d-flex align-items-center gap-1"><Icon icon="solar:phone-bold" className="text-secondary" /> {patient.phone}</span>
                                                <span className="text-xs text-secondary d-flex align-items-center gap-1"><Icon icon="solar:letter-bold" className="text-secondary" /> {patient.email}</span>
                                            </div>
                                        </td>
                                        <td className="text-secondary text-sm">{new Date(patient.createdAt).toLocaleDateString()}</td>
                                        <td className="text-center">
                                            <button onClick={() => handleViewHistory(patient)} className="btn btn-primary btn-sm px-16 radius-6 fw-medium d-flex align-items-center gap-2 mx-auto">
                                                <Icon icon="solar:eye-bold" /> View Details & History
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* HISTORY & DETAIL MODAL */}
            <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} centered size="xl" scrollable>
                <Modal.Header closeButton className="bg-base border-bottom py-3 px-4">
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                        <Icon icon="solar:user-id-bold" className="text-primary" />
                        Patient File: <span className="text-primary">{selectedPatient?.name}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {/* Patient Meta Data Section */}
                    <div className="p-24 border-bottom bg-primary-50">
                        <h6 className="text-sm text-uppercase fw-bold text-primary mb-3">Patient Visit Statistics</h6>

                        <div className="row g-3">
                            <div className="col-md-3">
                                <div className="bg-white p-3 radius-8 shadow-sm border-start border-primary border-4">
                                    <label className="text-xs text-secondary fw-bold text-uppercase">Total Visits</label>
                                    <h4 className="mb-0 fw-bold">{historyAppointments.length}</h4>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="bg-white p-3 radius-8 shadow-sm border-start border-success border-4">
                                    <label className="text-xs text-secondary fw-bold text-uppercase">Completed</label>
                                    <h4 className="mb-0 fw-bold text-success">{historyAppointments.filter(a => a.status === 'Completed').length}</h4>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="bg-white p-3 radius-8 shadow-sm border-start border-danger border-4">
                                    <label className="text-xs text-secondary fw-bold text-uppercase">Missed</label>
                                    <h4 className="mb-0 fw-bold text-danger">{historyAppointments.filter(a => a.status === 'Missed').length}</h4>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="bg-white p-3 radius-8 shadow-sm border-start border-info border-4">
                                    <label className="text-xs text-secondary fw-bold text-uppercase">Cancelled Upcoming</label>
                                    <h4 className="mb-0 fw-bold text-info">{historyAppointments.filter(a => a.status === 'Cancelled').length}</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-24">
                        <h6 className="text-sm text-uppercase fw-bold text-dark mb-16">Appointment History & Records</h6>
                        <div className="table-responsive radius-12 border overflow-hidden">
                            <table className="table table-striped mb-0">
                                <thead className="bg-base border-bottom">
                                    <tr>
                                        <th className="ps-4">Date / Time</th>
                                        <th>Doctor / Specialization</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th className="text-end pe-4">Prescription</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyLoading ? (
                                        <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                                    ) : historyAppointments.length === 0 ? (
                                        <tr><td colSpan="5" className="text-center py-4 text-secondary">No appointment records found for this patient.</td></tr>
                                    ) : (
                                        historyAppointments.map(apt => (
                                            <tr key={apt._id} className="align-middle">
                                                <td className="ps-4">
                                                    <span className="fw-bold d-block text-dark">{new Date(apt.date).toLocaleDateString()}</span>
                                                    <span className="text-secondary text-xs">{apt.timeSlot}</span>
                                                </td>
                                                <td>
                                                    <span className="fw-medium text-dark d-block">Dr. {apt.doctorId?.name.replace('Dr.', '')?.replace('dr.', '')?.replace('Mr.', '')?.replace('mr.', '')?.replace('Miss.', '')?.replace('miss.', '')?.replace('Mrs.', '')?.replace('mrs.', '')?.replace('Ms.', '')?.replace('ms.', '')}</span>
                                                    <span className="text-xs text-secondary">{apt.doctorId?.specialization}</span>
                                                </td>
                                                <td>
                                                    {apt.type === 'Online' ? (
                                                        <Badge bg="info-focus" className="fw-bold text-info-600 px-12 py-6 radius-4 border border-info-100">
                                                            <Icon icon="solar:videocamera-record-bold" className="me-1" /> ONLINE
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="warning-focus" className="fw-bold text-warning-600 px-12 py-6 radius-4 border border-warning-100">
                                                            <Icon icon="solar:walking-bold" className="me-1" /> PHYSICAL
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <Badge bg={apt.status === 'Completed' ? 'success' : apt.status === 'Upcoming' ? 'primary' : 'danger'} className="fw-normal">
                                                        {apt.status}
                                                    </Badge>
                                                </td>
                                                <td className="text-end pe-4">
                                                    {apt.status === 'Completed' && apt.hasPrescription && (
                                                        <Button size="sm" variant="outline-primary" className="radius-8 px-12 d-flex align-items-center gap-1 ms-auto" onClick={() => handleViewPrescription(apt._id)}>
                                                            <Icon icon="solar:file-text-bold" /> View Prescription
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-base py-3 px-4">
                    <Button variant="secondary" onClick={() => setShowHistoryModal(false)} className="px-24 radius-8">Close Patient File</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PatientManagement;
